import {
  getGoogleAnalyticsClientId, initializeAnalytics, isSupported, logEvent,
  setAnalyticsCollectionEnabled, setConsent, settings, type Analytics,
} from 'firebase/analytics';
import { getFirebaseClientApp } from '@/lib/firebase/client';
import { analyticsConsentGranted, subscribeSiteConsent } from './consent-client';
import { analyticsPage, cleanAnalyticsMetadata, type AnalyticsMetadata, type HomepageAnalyticsEvent } from './analytics-policy';
import { createAnalyticsLifecycle } from './analytics-lifecycle';

const measurementId = process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID;
const layerName = 'untangledAnalyticsLayer';
const gtagName = 'untangledGtag';
type TagWindow = Window & { [key: string]: unknown };
let instance: Analytics | undefined;
let controller: ReturnType<typeof createAnalyticsLifecycle> | undefined;
let subscribers = 0;
let cleanup: (() => void) | undefined;
let navigating = false;
let historyInstalled = false;
let pendingReady: { resolve: () => void; reject: (error: unknown) => void; timer?: ReturnType<typeof setTimeout> } | undefined;

function refreshReadiness(enabled: boolean) {
  const pending = pendingReady;
  if (!pending || !instance) return;
  clearTimeout(pending.timer);
  if (!enabled) return;
  const revision = controller?.currentRevision();
  // A get issued while opted out may never call back. Retry that public read on
  // re-grant, reusing the SDK and script; pause its timeout while consent is denied.
  pending.timer = setTimeout(() => pending.reject(new Error('Analytics unavailable')), 15_000);
  void getGoogleAnalyticsClientId(instance).then(() => pending.resolve()).catch(error => {
    if (controller?.accepts(revision) || (controller?.permitted() && revision === controller.currentRevision())) pending.reject(error);
  });
}

function consent(enabled: boolean) {
  setConsent({ analytics_storage: enabled ? 'granted' : 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
}

function getController() {
  if (typeof window === 'undefined' || !measurementId) return undefined;
  if (process.env.NEXT_PUBLIC_FIREBASE_USE_EMULATORS === 'true' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) return undefined;
  if (controller) return controller;
  const host = window as unknown as TagWindow;
  const permitted = () => !navigating && analyticsConsentGranted() && analyticsPage(window.location.href) !== null;
  controller = createAnalyticsLifecycle({
    supported: isSupported,
    initialize() {
      const app = getFirebaseClientApp();
      if (!app) throw new Error('Analytics configuration unavailable');
      // Firebase 11.9.1 writes this documented Google opt-out flag only AFTER
      // asynchronous config resolution. Its accessor enforces the CURRENT choice
      // even during that gap and on automatic SDK sends, history and storage reads.
      Object.defineProperty(host, `ga-disable-${measurementId}`, {
        configurable: true,
        get: () => !controller?.collecting(),
        set: () => { /* SDK writes cannot override current consent/route/readiness. */ },
      });
      const layer: unknown[] = [];
      host[layerName] = layer;
      host[gtagName] = function (...args: unknown[]) {
        const [command, id, parameters] = args;
        if (command === 'config' && id !== measurementId) {
          // Unexpected remote property configuration is never silently enabled.
          Object.defineProperty(host, `ga-disable-${String(id)}`, { configurable: true, get: () => true, set: () => {} });
          return;
        }
        if (command === 'consent') {
          // The SDK may deliver its initialization default after newer updates.
          args[2] = { analytics_storage: controller?.permitted() ? 'granted' : 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' };
        }
        if (command === 'event') {
          const params = parameters as Record<string, unknown>;
          if (!controller?.accepts(params?._untangledRevision)) return;
          const clean = { ...params };
          delete clean._untangledRevision;
          args[2] = clean;
        }
        // Preserve gtag's documented arguments-object queue format. This guarded
        // core also rechecks events AFTER Firebase's own asynchronous logEvent work.
        // eslint-disable-next-line prefer-rest-params -- gtag's documented queue format is an arguments object.
        Reflect.apply(function () { layer.push(arguments); }, null, args);
      };
      settings({ gtagName, dataLayerName: layerName });
      instance = initializeAnalytics(app, { config: {
        send_page_view: false,
        // Pin automatic defaults to a non-sensitive location/title. Explicit
        // events below use only the allowlisted path; referrers are never copied.
        page_location: `${window.location.origin}/`, page_referrer: '', page_title: 'The Untangled Life',
        ignore_referrer: true, allow_google_signals: false, allow_ad_personalization_signals: false,
      } });
    },
    async ready() {
      try {
        // This callback crosses Firebase config AND actual tag loading. Custom
        // events wait for it, rather than queueing into an unloaded tag.
        await new Promise<void>((resolve, reject) => {
          pendingReady = { resolve, reject };
          refreshReadiness(Boolean(controller?.collecting()));
        });
      } finally { clearTimeout(pendingReady?.timer); pendingReady = undefined; }
    },
    consent,
    collection(enabled) {
      if (instance) setAnalyticsCollectionEnabled(instance, enabled);
      refreshReadiness(enabled);
    },
    event(name, params) { if (instance) logEvent(instance, name, params); },
  }, permitted);
  return controller;
}

export function mountAnalyticsConsent() {
  const lifecycle = getController();
  if (!lifecycle) return () => {};
  subscribers++;
  if (subscribers === 1) {
    const sync = () => lifecycle.sync();
    const unsubscribe = subscribeSiteConsent(sync);
    if (!historyInstalled) {
      historyInstalled = true;
      const originals = { pushState: history.pushState, replaceState: history.replaceState };
      const wrappers = {} as typeof originals;
      for (const method of ['pushState', 'replaceState'] as const) {
        wrappers[method] = function (...args: Parameters<History['pushState']>) {
          // Installed before tag loading: deny while any history wrapper observes
          // the new URL, rather than waiting for a React route effect.
          navigating = true;
          lifecycle.sync();
          try { return originals[method].apply(history, args); }
          finally { navigating = false; lifecycle.sync(); }
        };
        history[method] = wrappers[method];
      }
    }
    window.addEventListener('popstate', sync);
    window.addEventListener('hashchange', sync);
    const visibility = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', visibility);
    cleanup = () => {
      unsubscribe();
      window.removeEventListener('popstate', sync);
      window.removeEventListener('hashchange', sync);
      document.removeEventListener('visibilitychange', visibility);
      // The two history guards live for this document, like the loaded tag.
      // Never stack another pair on remount, or overwrite Next/gtag wrappers.
      lifecycle.stop();
    };
    lifecycle.start();
  }
  let mounted = true;
  return () => { if (mounted) { mounted = false; if (--subscribers === 0) { cleanup?.(); cleanup = undefined; } } };
}

export async function trackAnalyticsEvent(name: HomepageAnalyticsEvent, metadata?: AnalyticsMetadata) {
  const lifecycle = getController();
  if (!lifecycle || !analyticsConsentGranted()) return;
  const page = analyticsPage(window.location.href);
  if (page !== '/') return; // Existing events describe homepage activity only.
  await lifecycle.event(name, {
    ...cleanAnalyticsMetadata(metadata),
    page_location: `${window.location.origin}${page}`, page_referrer: '', page_title: 'The Untangled Life',
  });
}
